import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError, errorMessage } from '@/lib/api';
import type { CustomFieldDef, CustomFieldType } from '@shared/types';
import { Callout, Spinner } from './bits';
import { DialogBody, DialogCancel, DialogFoot, DialogHead } from './Dialog';
import { Field, InputField, SelectField } from './Field';
import { Icon } from './Icon';
import type { IconName } from './icons';

// Adding a custom field, or renaming one and editing its choices: one form for people, companies and deals. It is
// a dialog of its own, or sits inline in a form that is already a dialog (the deal form).

export const FIELD_TYPES: { value: CustomFieldType; label: string; icon: IconName }[] = [
  { value: 'text', label: 'Text', icon: 'note' },
  { value: 'number', label: 'Number', icon: 'hash' },
  { value: 'date', label: 'Date', icon: 'calendar' },
  { value: 'select', label: 'One choice', icon: 'list' },
  { value: 'multiselect', label: 'Several choices', icon: 'layers' },
  { value: 'url', label: 'Web address', icon: 'link' },
  { value: 'checkbox', label: 'Yes or no', icon: 'check-circle' },
];

export const fieldType = (t: CustomFieldType) => FIELD_TYPES.find((x) => x.value === t) ?? FIELD_TYPES[0]!;
export const isChoiceType = (t: CustomFieldType) => t === 'select' || t === 'multiselect';

/** What the form saves. `renames` maps a saved choice to its new name, so records holding it keep it. */
export interface FieldDraft {
  label: string;
  type: CustomFieldType;
  options: string[];
  renames: Record<string, string>;
}

interface Choice {
  id: number;
  value: string;
  /** The saved name, for choices the field already has. */
  original: string | null;
}

type Errors = { label?: string; type?: string; options?: string; form?: string };

let choiceSeq = 1;
const blank = (): Choice => ({ id: choiceSeq++, value: '', original: null });

export function CustomFieldForm({
  field,
  noun,
  title,
  sub,
  glyph,
  placeholder = 'Such as Budget cycle',
  inline,
  cancelLabel,
  onSave,
  onCancel,
  onClose,
}: {
  field: CustomFieldDef | null;
  /** What carries the field, for the messages: "person", "company" or "deal". */
  noun: string;
  /** The dialog title; inline, the form's name for screen readers. */
  title: string;
  /** The dialog subtitle; inline, a note beside the buttons. */
  sub?: ReactNode;
  glyph?: IconName;
  placeholder?: string;
  /** Inside another dialog: no head of its own, and Save is not the dialog's main action. */
  inline?: boolean;
  cancelLabel?: string;
  /** Saves the draft. Field messages of an ApiError show under their fields, anything else beside the buttons. */
  onSave: (draft: FieldDraft) => Promise<void>;
  onCancel: () => void;
  /** The dialog's close button. */
  onClose?: () => void;
}) {
  const [label, setLabel] = useState(field?.label ?? '');
  const [type, setType] = useState<CustomFieldType>(field?.type ?? 'text');
  const [choices, setChoices] = useState<Choice[]>(() =>
    field && isChoiceType(field.type) && field.options.length ? field.options.map((o) => ({ id: choiceSeq++, value: o, original: o })) : [blank()],
  );
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [focusChoice, setFocusChoice] = useState<number | null>(null);
  const labelRef = useRef<HTMLInputElement>(null);
  const choicesRef = useRef<HTMLDivElement>(null);
  const base = useId();
  const choice = isChoiceType(type);
  const removedInUse = field && choice ? field.options.filter((o) => !choices.some((c) => c.original === o)) : [];

  useEffect(() => {
    if (inline) labelRef.current?.focus();
  }, [inline]);

  useEffect(() => {
    if (focusChoice === null) return;
    choicesRef.current?.querySelector<HTMLInputElement>(`[data-choice="${focusChoice}"]`)?.focus();
    setFocusChoice(null);
  }, [focusChoice]);

  const addChoice = (after?: number) => {
    const c = blank();
    setChoices((list) => {
      const at = after === undefined ? list.length : list.findIndex((x) => x.id === after) + 1;
      return [...list.slice(0, at), c, ...list.slice(at)];
    });
    setFocusChoice(c.id);
  };

  const removeChoice = (id: number) => {
    const i = choices.findIndex((c) => c.id === id);
    const next = choices.filter((c) => c.id !== id);
    setChoices(next);
    const neighbour = next[Math.min(i, next.length - 1)];
    if (neighbour) setFocusChoice(neighbour.id);
    setErrors((e) => ({ ...e, options: undefined, form: undefined }));
  };

  const onChoiceKey = (e: KeyboardEvent<HTMLInputElement>, c: Choice) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (c.value.trim()) addChoice(c.id);
    } else if (e.key === 'Backspace' && !c.value && choices.length > 1) {
      e.preventDefault();
      removeChoice(c.id);
    }
  };

  const validate = (): FieldDraft | null => {
    const next: Errors = {};
    const name = label.trim().replace(/\s+/g, ' ');
    if (!name) next.label = 'Name the field';
    else if (name.length > 60) next.label = 'Keep the name under 60 characters';
    const options = choice ? choices.map((c) => c.value.trim()).filter(Boolean) : [];
    if (choice) {
      if (options.length === 0) next.options = 'Add at least one choice';
      else if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) next.options = 'Each choice must be different';
      else if (options.some((o) => o.length > 60)) next.options = 'Keep each choice under 60 characters';
      else if (options.length > 50) next.options = 'Keep it to 50 choices';
    }
    setErrors(next);
    if (next.label) labelRef.current?.focus();
    if (next.label || next.options) return null;
    const renames: Record<string, string> = {};
    for (const c of choices) if (c.original !== null && c.value.trim() && c.value.trim() !== c.original) renames[c.original] = c.value.trim();
    return { label: name, type, options, renames };
  };

  const save = async () => {
    if (busy) return;
    const draft = validate();
    if (!draft) return;
    setBusy(true);
    try {
      await onSave(draft);
    } catch (err) {
      const f = err instanceof ApiError ? err.fields : {};
      const options = Object.entries(f).find(([k]) => k === 'options' || k.startsWith('options.'))?.[1];
      const known = f.label || f.type || options;
      setErrors({ label: f.label, type: f.type, options, form: known ? undefined : errorMessage(err) });
      if (f.label) labelRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const fields = (
    <div className="cff">
      <InputField
        ref={labelRef}
        label="Name"
        value={label}
        maxLength={60}
        placeholder={placeholder}
        error={errors.label}
        required
        autoComplete="off"
        data-autofocus={!inline || undefined}
        onChange={(e) => {
          setLabel(e.target.value);
          setErrors((x) => ({ ...x, label: undefined, form: undefined }));
        }}
        onKeyDown={(e) => {
          if (inline && e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            void save();
          }
        }}
      />
      <SelectField
        label="Type"
        value={type}
        disabled={!!field}
        help={field ? 'A field keeps the type it was created with.' : undefined}
        error={errors.type}
        options={FIELD_TYPES.map((t) => ({ value: t.value, label: t.label }))}
        onChange={(e) => {
          setType(e.target.value as CustomFieldType);
          setErrors((x) => ({ ...x, type: undefined, options: undefined, form: undefined }));
        }}
      />
      {choice ? (
        <Field
          label="Choices"
          className="cff-wide"
          htmlFor={`${base}-choice`}
          error={errors.options}
          help={`Press Enter for the next choice. Renaming a choice keeps it on every ${noun} that has it.`}
        >
          <div ref={choicesRef} className="cff-choices">
            {choices.map((c, i) => (
              <div key={c.id} className="cff-choice">
                <input
                  id={i === 0 ? `${base}-choice` : undefined}
                  className="input"
                  data-choice={c.id}
                  value={c.value}
                  maxLength={60}
                  aria-label={`Choice ${i + 1}`}
                  aria-invalid={errors.options ? true : undefined}
                  placeholder={i === 0 ? 'First choice' : 'Another choice'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setChoices((list) => list.map((x) => (x.id === c.id ? { ...x, value: v } : x)));
                    setErrors((x) => ({ ...x, options: undefined, form: undefined }));
                  }}
                  onKeyDown={(e) => onChoiceKey(e, c)}
                />
                {c.original && c.value.trim() && c.value.trim() !== c.original ? <span className="cff-was">was {c.original}</span> : null}
                <button
                  type="button"
                  className="icon-btn xs"
                  aria-label={`Remove ${c.value.trim() || `choice ${i + 1}`}`}
                  data-tip="Remove choice"
                  disabled={choices.length === 1}
                  onClick={() => removeChoice(c.id)}
                >
                  <Icon name="x" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-ghost btn-sm cff-add" onClick={() => addChoice()}>
            <Icon name="plus" />
            Add a choice
          </button>
        </Field>
      ) : null}
      {removedInUse.length ? (
        <Callout tone="warn" className="cff-wide">
          Every {noun} set to {removedInUse.map((o) => `“${o}”`).join(', ')} loses that value.
        </Callout>
      ) : null}
    </div>
  );

  const note = errors.form ? (
    <span className="note err" role="alert">
      <Icon name="warn" />
      {errors.form}
    </span>
  ) : null;
  const saveIcon = busy ? <Spinner label="Saving" /> : <Icon name={field ? 'check' : 'plus'} />;
  const saveLabel = field ? 'Save field' : 'Add field';

  if (inline)
    return (
      <div
        className="cff-inline"
        role="group"
        aria-label={title}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
      >
        {fields}
        <div className="cff-foot">
          {note ?? (sub ? <span className="note">{sub}</span> : null)}
          <span className="spacer" />
          <DialogCancel onClick={onCancel}>{cancelLabel}</DialogCancel>
          <button type="button" className="btn btn-tile" disabled={busy} onClick={() => void save()}>
            {saveIcon}
            {saveLabel}
          </button>
        </div>
      </div>
    );

  return (
    <form
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void save();
      }}
    >
      <DialogHead title={title} sub={sub} glyph={glyph} onClose={onClose ?? onCancel} />
      <DialogBody>{fields}</DialogBody>
      <DialogFoot>
        {note}
        <span className="spacer" />
        <DialogCancel onClick={onCancel}>{cancelLabel}</DialogCancel>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {saveIcon}
          {saveLabel}
        </button>
      </DialogFoot>
    </form>
  );
}
